import { useCallback, useEffect, useState } from 'react';
import { bankAccountsAPI } from 'services/swagger-api';
import { DropdownItem } from 'components/forms';

export interface BankAccountOptions {
  options: DropdownItem[];
  isLoading: boolean;
  /** Compte par défaut de la personne, à présélectionner sur un formulaire vierge. */
  defaultAccountId: string | null;
}

/** IBAN par groupes de quatre : illisible d'un bloc, mémorisable ainsi. */
const formatIban = (iban?: string | null): string =>
  (iban ?? '').replace(/(.{4})/g, '$1 ').trim();

/**
 * Libellé d'un compte : « Compte receveur — FR76 1027 8041 … ».
 *
 * Le libellé seul ne suffit pas à choisir quand un titulaire a plusieurs comptes nommés
 * de la même façon, et l'IBAN seul ne dit pas à quoi le compte sert : les deux sont
 * portés par le label plutôt que par un texte discret, qui disparaîtrait une fois la
 * sélection faite.
 */
export const bankAccountLabel = (label?: string | null, iban?: string | null): string => {
  const formattedIban = formatIban(iban);
  if (label && formattedIban) return `${label} — ${formattedIban}`;
  return label || formattedIban || 'Compte sans libellé';
};

/**
 * Comptes bancaires d'une personne, rechargés à chaque changement de titulaire.
 *
 * L'ancien back-office répétait ce chargement à quatre endroits (assureur principal,
 * co-assureur, ré-assureur, bénéficiaire de frais) avec à chaque fois sa propre gestion
 * de l'activation/désactivation du champ et de la présélection du compte par défaut.
 */
export const useBankAccountOptions = (personId?: string | null): BankAccountOptions => {
  const [options, setOptions] = useState<DropdownItem[]>([]);
  const [defaultAccountId, setDefaultAccountId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!personId) {
      setOptions([]);
      setDefaultAccountId(null);
      return;
    }

    setIsLoading(true);
    try {
      const response = await bankAccountsAPI.getBankAccountsByPersonId({ personId });
      const accounts = response.bankAccounts ?? [];

      setOptions(
        accounts.map((account) => ({
          key: account.id as string,
          label: bankAccountLabel(account.label, account.iban),
        }))
      );
      setDefaultAccountId(accounts.find((account) => account.isDefault)?.id ?? null);
    } catch (error) {
      console.error('Chargement des comptes bancaires impossible', error);
      setOptions([]);
      setDefaultAccountId(null);
    } finally {
      setIsLoading(false);
    }
  }, [personId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { options, isLoading, defaultAccountId };
};
